import { Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { conditionallyPluralize } from '../../utils/text-utils';
import {
  GroupedMappingRow,
  MappingTabConfig,
  UnassignedMappingRow
} from './client-data-mapping-constants';
import { useClientTargetPicker } from './ClientTargetPicker/ClientTargetPicker';

interface BulkAssignMode {
  mode: 'assign';
  rows: UnassignedMappingRow[];
}

interface BulkReassignMode {
  mode: 'reassign';
  // Each row must carry exactly one cid -- the caller (MappingTable) gates the "Reassign N
  // selected" button on this, so per-row fromCid is unambiguous. Multi-cid rows are not offered
  // for bulk reassign (matching the per-row Reassign, which is also gated to single-cid rows).
  rows: GroupedMappingRow[];
}

type BulkCompanyPickerMode = BulkAssignMode | BulkReassignMode;

interface CommonProps {
  tab: MappingTabConfig;
  onHide: () => void;
  // Fire-and-forget: the modal collects target cids and closes; the parent wraps the actual
  // batched API-call chain in optimisticMutate. See ClientDataMapping.handleBulkAssignSubmit
  // / handleBulkReassignSubmit for the API-call shape (assign: fan out per target cid;
  // reassign: one batched reassign to the first target + follow-on assigns for the rest).
  onSubmit: (targetCids: string[]) => void;
}

type BulkCompanyPickerModalProps = CommonProps & BulkCompanyPickerMode;

// Bulk analogue of CompanyPickerModal -- runs the same picker UI against N items instead of one.
// Mark-as-generic is intentionally suppressed here -- generic is a single-item audit decision
// (email_domain only), not something to bulk-apply, and mixing it with a bulk flow makes the
// blast radius hard to read.
export const BulkCompanyPickerModal = ({
  tab,
  onHide,
  onSubmit,
  ...modeProps
}: BulkCompanyPickerModalProps) => {
  const target = useClientTargetPicker({
    tab,
    checkboxId: 'bulk-company-picker-mark-generic',
    showOrWrapper: false,
    hideMarkGeneric: true
  });

  const itemCount = modeProps.rows.length;
  const isReassign = modeProps.mode === 'reassign';

  const handleSave = () => {
    if (!target.isValid) return;
    onSubmit(target.targetCids);
    onHide();
  };

  const noun = conditionallyPluralize(itemCount, 'mapping');
  const title = isReassign ? `Reassign ${itemCount} ${noun}` : `Assign ${itemCount} ${noun}`;
  const submitLabel = isReassign ? 'Reassign' : 'Assign';

  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{target.render()}</Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button disabled={!target.isValid} onClick={handleSave}>
          {submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
